import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import Loading from '../Common/Loading';

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

interface Suburb {
  Suburbcode: number;
  Suburbname: string;
  Postcode: string;
}

const EL_SelectSuburb: React.FC = () => {
  const location = useLocation();

  // ── Init state ──────────────────────────────────────────────────────────────
  const [country, setCountry] = useState('');
  const [stateName, setStateName] = useState('');
  const [stateCode, setStateCode] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // ── Search state ────────────────────────────────────────────────────────────
  const [searchCriteria, setSearchCriteria] = useState<'suburb' | 'postcode'>('postcode');
  const [suburb, setSuburb] = useState('');
  const [postCode, setPostCode] = useState('');
  const [tmpSuburb, setTmpSuburb] = useState('');
  const [tmpPostCode, setTmpPostCode] = useState('');

  // ── Results state ───────────────────────────────────────────────────────────
  const [suburbs, setSuburbs] = useState<Suburb[]>([]);
  const [selectedSuburb, setSelectedSuburb] = useState<Suburb | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(20);
  const [totalRecords, setTotalRecords] = useState(0);
  const [showOkButton, setShowOkButton] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // ── URL params ──────────────────────────────────────────────────────────────
  const searchParams = new URLSearchParams(location.search);
  const stateCodeParam = searchParams.get('StateCode') || '';
  const stateNameParam = searchParams.get('StateName') || '';
  const countryParam = searchParams.get('Country') || '';
  const fromParam = searchParams.get('From') || '';

  // ── Initialize ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const initialize = async () => {
      setIsLoading(true);
      try {
        const params = new URLSearchParams({
          StateCode: stateCodeParam,
          StateName: stateNameParam,
          Country: sessionStorage.getItem('country') || countryParam,
          From: fromParam,
          CalledFromAdmin: 'YES',
          login: sessionStorage.getItem('login') || '',
        });

        const response = await fetch(
          `${API_BASE_URL}api/select_suburb?${params.toString()}`,
          { credentials: 'include' }
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const data = await response.json();
        if (data.success) {
          setCountry(data.country);
          setStateName(data.stateName);
          setStateCode(data.stateCode || stateCodeParam);
          setErrorMsg(data.errorMsg || '');
          setSearchCriteria(data.fromTextBox === 'S' ? 'suburb' : 'postcode');
        } else {
          setErrorMsg(data.error || 'Failed to initialize');
        }
      } catch (err) {
        setErrorMsg('Failed to initialize. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    initialize();
    document.title = 'Select Suburb';
    document.body.style.backgroundColor = '#EEF1F9';
    document.body.style.margin = '0px';
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (errorMsg) { alert(errorMsg); setErrorMsg(''); }
  }, [errorMsg]);

  const loadSuburbs = useCallback(
    async (page: number, srchSuburb: string, srchPostCode: string) => {
      setIsLoading(true);
      try {
        const startRecNo = (page - 1) * rowsPerPage + 1;
        const params = new URLSearchParams({
          StartRecNo: startRecNo.toString(),
          RowsPerPage: rowsPerPage.toString(),
          Suburb: srchSuburb,
          PostCode: srchPostCode,
          StateCode: stateCode,
          StateName: stateName,
          Country: country,
        });

        const response = await fetch(
          `${API_BASE_URL}api/populate_suburbs?${params.toString()}`,
          { credentials: 'include' }
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const data = await response.json();
        if (data.success) {
          setSuburbs(data.suburbs);
          setCurrentPage(page);
          setTotalRecords(data.totalRecords);
          setSelectedSuburb(null);
          setShowOkButton(false);
        } else {
          setErrorMsg(data.error || 'Failed to load suburbs');
        }
      } catch (err) {
        setErrorMsg('Failed to load suburbs. Please try again.');
      } finally {
        setIsLoading(false);
      }
    },
    [stateCode, stateName, country, rowsPerPage]
  );

  const handleSearch = (e: React.MouseEvent) => {
    e.preventDefault();
    setHasSearched(true);
    loadSuburbs(1, suburb, postCode);
  };

  const handleAdd = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (searchCriteria === 'suburb' && (!tmpPostCode.trim() || !suburb.trim())) {
      alert('Please enter all the feilds'); return;
    }
    if (searchCriteria === 'postcode' && (!tmpSuburb.trim() || !postCode.trim())) {
      alert('Please enter all the feilds'); return;
    }

    setIsLoading(true);
    try {
      const suburbToAdd = searchCriteria === 'suburb' ? suburb : tmpSuburb;
      const postcodeToAdd = searchCriteria === 'suburb' ? tmpPostCode : postCode;

      const response = await fetch(`${API_BASE_URL}api/add_suburb/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ country, stateCode, stateName, suburb: suburbToAdd, postcode: postcodeToAdd, searchCriteria }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const result = await response.json();
      if (result.success) {
        if (result.addNew === 'N') {
          alert('The Combination of Suburb and postcode already exists');
        } else if (result.addNew === 'Y') {
          alert('Suburb and postcode added successfully');
          updateParentWindow(result.suburb, result.postCode);
        }
      } else {
        setErrorMsg(result.error || 'Failed to add suburb');
      }
    } catch (err) {
      setErrorMsg('Failed to add suburb. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectSuburb = (s: Suburb) => {
    setSelectedSuburb(s);
    setShowOkButton(true);
  };

  const handleOk = (e: React.MouseEvent) => {
    e.preventDefault();
    if (selectedSuburb) updateParentWindow(selectedSuburb.Suburbname, selectedSuburb.Postcode);
  };

  const updateParentWindow = (suburbName: string, pc: string) => {
    try {
      if (window.opener) (window.opener as any).ret = `${suburbName}|${pc}`;
    } catch (_) { }
    window.close();
  };

  const handleClose = (e: React.MouseEvent) => {
    e.preventDefault();
    try {
      if (window.opener && typeof (window.opener as any).gfn_ClosePopup === 'function') {
        (window.opener as any).gfn_ClosePopup();
      }
    } catch (_) { }
    window.close();
  };

  const handleSelectCriteria = (criteria: 'suburb' | 'postcode') => {
    setSearchCriteria(criteria);
    if (criteria === 'suburb') { setPostCode(''); setTmpSuburb(''); }
    else { setSuburb(''); setTmpPostCode(''); }
  };

  const totalPages = Math.max(1, Math.ceil(totalRecords / rowsPerPage));

  const handlePageNav = (page: number) => {
    if (page < 1 || page > totalPages) return;
    loadSuburbs(page, suburb, postCode);
  };

  const getRowNumber = (index: number) => (currentPage - 1) * rowsPerPage + index + 1;

  const showAddSuburbRow = searchCriteria === 'postcode';
  const showAddZipRow = true;
  // const showAddZipRow = searchCriteria === 'suburb' || searchCriteria === 'postcode';

  if (isLoading) return <Loading />;

  return (
    <div style={{ width: '100%', margin: 0, backgroundColor: '#EEF1F9', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: '13px' }}>

      <table id="tblSelectSuburb" width="100%" cellPadding={5} cellSpacing={0}
        style={{ borderCollapse: 'collapse' }}>
        <tbody>

          <tr>
            <td style={{
              width: '20%',
              borderTop: '2px solid white', borderBottom: '2px solid white',
              borderLeft: '2px solid white', backgroundColor: '#AAD5FF',
              padding: '5px 5px 5px 10px',
            }}>
              &nbsp;Country:&nbsp;<span style={{ color: 'red' }}>{country}</span>
            </td>
            <td style={{
              width: '25%',
              borderTop: '2px solid white', borderBottom: '2px solid white',
              backgroundColor: '#AAD5FF', textAlign: 'right', padding: '5px',
            }}>
              &nbsp;&nbsp;State:
            </td>
            <td style={{
              width: '55%',
              borderTop: '2px solid white', borderBottom: '2px solid white',
              borderRight: '2px solid white', backgroundColor: '#AAD5FF', padding: '5px',
            }}>
              <span style={{ color: 'red' }}>{stateName}</span>&nbsp;
            </td>
          </tr>

          <tr>
            <td style={{ width: '20%', borderLeft: '2px solid white', padding: '9px 5px 9px 10px' }}>
              <input
                id="rdoSuburb" type="radio" name="Search"
                checked={searchCriteria === 'suburb'}
                onChange={() => handleSelectCriteria('suburb')}
              />
              &nbsp;Suburb&nbsp;
            </td>
            <td colSpan={2} style={{ borderRight: '2px solid white', padding: '5px', verticalAlign: 'middle' }}>
              <input
                type="text" value={suburb}
                onChange={e => setSuburb(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSearch(e as any); }}
                style={{ ...inputStyle, display: searchCriteria === 'suburb' ? 'inline-block' : 'none' }}
              />
            </td>
          </tr>

          <tr>
            <td style={{ width: '20%', borderBottom: '2px solid white', borderLeft: '2px solid white', padding: '9px 5px 9px 10px' }}>
              <input
                id="rdoPostCode" type="radio" name="Search"
                checked={searchCriteria === 'postcode'}
                onChange={() => handleSelectCriteria('postcode')}
              />
              &nbsp;Zip Code&nbsp;
            </td>
            <td colSpan={2} style={{ borderBottom: '2px solid white', borderRight: '2px solid white', padding: '5px', verticalAlign: 'middle' }}>
              <input
                type="text" value={postCode}
                onChange={e => setPostCode(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSearch(e as any); }}
                style={{ ...inputStyle, display: searchCriteria === 'postcode' ? 'inline-block' : 'none' }}
              />
            </td>
          </tr>

          {showAddSuburbRow && (
            <tr id="trTmpSuburb">
              <td style={{ width: '20%', padding: '5px 5px 5px 10px', fontSize: "12px" }}>Add Suburb</td>
              <td colSpan={2} style={{ padding: '5px' }}>
                <input type="text" value={tmpSuburb}
                  onChange={e => setTmpSuburb(e.target.value)}
                  style={inputStyle}
                />
              </td>
            </tr>
          )}

          {showAddZipRow && (
            <tr id="trTmpPostcode">
              <td style={{ width: '20%', padding: '5px 5px 5px 10px' }}>Add Zip Code</td>
              <td colSpan={2} style={{ padding: '5px' }}>
                <input type="text" value={tmpPostCode}
                  onChange={e => setTmpPostCode(e.target.value)}
                  style={inputStyle}
                />
              </td>
            </tr>
          )}

          <tr>
            <td colSpan={3} style={{ border: '2px solid white', padding: '7px', textAlign: 'center' }}>
              <button onClick={handleAdd} style={btnStyle}>Add</button>
              &nbsp;&nbsp;&nbsp;
              <button onClick={handleSearch} style={btnStyle}>Search</button>
              &nbsp;&nbsp;&nbsp;
              <button onClick={handleClose} style={btnStyle}>Close</button>
            </td>
          </tr>

          <tr>
            <td colSpan={3} style={{ textAlign: 'center', padding: '4px' }}>
              <span style={{ color: 'red', fontSize: '10px', fontWeight: 'bold' }}>*</span>
              &nbsp;<span style={{ fontSize: "10px" }}>Please choose a Suburb / Post Code and press OK</span>
            </td>
          </tr>

        </tbody>
      </table>

      <div id="divResultTable" style={{ width: '100%', margin: '0', padding: '3px', boxSizing: 'border-box' }}>
        <table width="100%" cellPadding={0} cellSpacing={0} style={{ borderCollapse: 'collapse' }}>
          <tbody>
            <tr>
              <td>
                <div style={{ overflowX: 'hidden' }}>
                  <table width="100%" cellPadding={0} cellSpacing={0}
                    style={{ borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#c7ddfd', border: "1px solid #80A0FF" }}>
                        <th style={{ ...thStyle, width: '5%', textAlign: 'center' }}>#</th>
                        <th style={{ ...thStyle, width: '25%' }}>Suburb</th>
                        <th style={{ ...thStyle, width: '65%' }}>Zip Code</th>

                      </tr>
                    </thead>
                  </table>
                </div>

                {/* Scrollable body */}
                <div style={{
                  overflowY: 'auto', overflowX: 'auto',
                  height: '380px', width: '100%',
                  borderLeft: '1px solid #c8d8e8',
                  borderRight: '1px solid #c8d8e8',
                  borderBottom: '1px solid #c8d8e8',
                }}>
                  <table width="100%" cellPadding={0} cellSpacing={0}
                    style={{ borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                    <tbody>
                      {suburbs.length === 0 && hasSearched ? (
                        <tr>
                          <td colSpan={3} style={{ padding: '12px', textAlign: 'center', color: '#666' }}>
                            No records found.
                          </td>
                        </tr>
                      ) : (
                        suburbs.map((s, index) => {
                          const isSelected = selectedSuburb?.Suburbcode === s.Suburbcode && selectedSuburb?.Postcode === s.Postcode;
                          return (
                            <tr key={`${s.Suburbcode}-${index}`}
                              onClick={() => handleSelectSuburb(s)}
                              style={{
                                cursor: 'pointer',
                                backgroundColor: isSelected ? 'orange' : (index % 2 === 0 ? '#f4faff' : '#ffffff'),
                                color: isSelected ? 'white' : 'black',
                              }}
                            >
                              <td
                                style={{
                                  ...tdStyle,
                                  width: '5%',
                                  textAlign: 'center'
                                }}
                              >
                                {getRowNumber(index)}
                              </td>

                              <td
                                style={{
                                  ...tdStyle,
                                  width: '25%'
                                }}
                              >
                                {s.Suburbname}
                              </td>

                              <td
                                style={{
                                  ...tdStyle,
                                  width: '65%'
                                }}
                              >
                                {s.Postcode}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ═══ OK Button — appears after row selected ════════════════════════════ */}
      {showOkButton && (
        <div style={{ padding: '5px 0 10px', textAlign: 'center' }}>
          <table id="tblOKButton" width="100%" cellPadding={7} cellSpacing={0}
            style={{ border: '2px solid white', borderCollapse: 'collapse' }}>
            <tbody>
              <tr style={{ backgroundColor: '#D8E8F8' }}>
                <td style={{ textAlign: 'center' }}>
                  <input id="btnOK" type="button" value="OK" onClick={handleOk}
                    style={{ ...btnStyle, width: '70px' }} />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      {/* ═══ Pagination footer ═════════════════════════════════════════════════ */}
      {totalRecords > 0 && (
        <div style={{
          backgroundColor: '#c7ddfd',
          border: '2px solid #80A0FF',
          padding: '6px 0',
          textAlign: 'center',
          fontSize: '13px',
          position: 'sticky',
          bottom: 0,
        }}>
          <span onClick={() => handlePageNav(1)} style={navBtn(currentPage > 1)}>&lt;&lt;</span>
          &nbsp;
          <span onClick={() => handlePageNav(currentPage - 1)} style={navBtn(currentPage > 1)}>&lt;</span>
          &nbsp;
          <span style={{ color: '#000' }}>{currentPage} of {totalPages}</span>
          &nbsp;
          <span onClick={() => handlePageNav(currentPage + 1)} style={navBtn(currentPage < totalPages)}>&gt;</span>
          &nbsp;
          <span onClick={() => handlePageNav(totalPages)} style={navBtn(currentPage < totalPages)}>&gt;&gt;</span>
        </div>
      )}

    </div>
  );
};


const inputStyle: React.CSSProperties = {
  width: '70%',
  border: '1px solid gray',
  padding: '2px 4px',
  fontFamily: 'Arial, Helvetica, sans-serif',
  fontSize: '13px',
  boxSizing: 'border-box',
};

const btnStyle: React.CSSProperties = {
  width: '70px',
  padding: '4px 4px',
  border: '2px solid orange',
  backgroundColor: '#D8D8FF',
  cursor: 'pointer',
  fontFamily: 'Arial, Helvetica, sans-serif',
  fontSize: '10px',
};

const thStyle: React.CSSProperties = {
  padding: '8px 5px',
  textAlign: 'left',
  fontWeight: 'normal',
  borderRight: '1px solid #80A0FF',
  borderBottom: '1px solid #80A0FF',
  fontSize: '12px',
  fontFamily: 'Arial, Helvetica, sans-serif',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  color: '#2859ac',
  boxSizing: 'border-box'
};

const tdStyle: React.CSSProperties = {
  padding: '6px 5px',
  borderRight: '1px solid #80A0FF',
  fontSize: '13px',
  fontFamily: 'Arial, Helvetica, sans-serif',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  boxSizing: 'border-box'
};

const navBtn = (enabled: boolean): React.CSSProperties => ({
  color: enabled ? 'blue' : '#777777',
  cursor: enabled ? 'pointer' : 'default',
  textDecoration: enabled ? 'underline' : 'none',
  userSelect: 'none',
  fontFamily: 'Arial, Helvetica, sans-serif',
  fontSize: '13px',
});

export default EL_SelectSuburb;